// Buyer Assistant: answers grounded questions about the marketplace using
// the Lovable AI Gateway. Never invents auctions/sellers/prices — the model
// is only given real context we fetch. Advisory-only, disclaimer added.

import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

const AskInput = z.object({ question: z.string().min(3).max(500) });

export const askBuyerAssistant = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => AskInput.parse(d))
  .handler(async ({ data, context }) => {
    // Fetch a small context window of active auctions the user is likely
    // asking about (matches keywords from the question).
    const keywords = data.question
      .toLowerCase()
      .replace(/[^a-z0-9 ]+/g, " ")
      .split(/\s+/)
      .filter((w) => w.length > 3)
      .slice(0, 4);

    let q = context.supabase
      .from("auctions")
      .select("id, title, card_set, current_bid_pence, starting_price_pence, end_time, grading_company, grade")
      .eq("status", "active")
      .limit(8);
    if (keywords.length > 0) q = q.or(keywords.map((k) => `title.ilike.%${k}%`).join(","));
    const { data: rows } = await q;

    const apiKey = process.env.LOVABLE_API_KEY;
    if (!apiKey) {
      return {
        answer:
          "Buyer assistant is not fully configured yet. Meanwhile, I can show you matching live listings below.",
        context: rows ?? [],
        disclaimer: "Advisory only — not financial advice.",
      };
    }
    const contextText = (rows ?? [])
      .map(
        (r) =>
          `- ${r.title} (set: ${r.card_set ?? "n/a"}, grade: ${r.grading_company ?? "raw"} ${r.grade ?? ""}, current: £${((r.current_bid_pence ?? r.starting_price_pence ?? 0) / 100).toFixed(2)}, ends ${r.end_time})`,
      )
      .join("\n");

    const resp = await fetch("https://ai.gateway.lovable.dev/v1/chat/completions", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${apiKey}` },
      body: JSON.stringify({
        model: "google/gemini-2.5-flash",
        messages: [
          {
            role: "system",
            content:
              "You are the BidNova buyer assistant. Only reason from the provided listings and general trading-card knowledge. Never fabricate prices, sellers, or authenticity claims. Always end with a one-line disclaimer: 'Advisory only — not financial advice.'",
          },
          { role: "user", content: `Question: ${data.question}\n\nActive listings:\n${contextText || "(none matched)"}` },
        ],
      }),
    });
    if (!resp.ok) {
      return {
        answer: "The assistant is temporarily unavailable. Try browsing the live listings shown below.",
        context: rows ?? [],
        disclaimer: "Advisory only — not financial advice.",
      };
    }
    const json = (await resp.json()) as { choices?: Array<{ message?: { content?: string } }> };
    const answer = json.choices?.[0]?.message?.content ?? "";
    return { answer, context: rows ?? [], disclaimer: "Advisory only — not financial advice." };
  });
